import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, QrCode, Zap, Users, LogOut, Flame } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { OffersStrip } from "@/components/OffersStrip";
import { weeklyCount, resetLabel } from "@/lib/streak";

const ACTIONS = [
  { label: "Find quizzes near me", sub: "Venues & live rooms", icon: Compass, to: "/search", cls: "bg-amber-400 text-slate-950 shadow-[0_0_25px_rgba(245,158,11,0.35)]", testid: "home-find-btn" },
  { label: "Scan QR code", sub: "Jump into a table quiz", icon: QrCode, to: "/qr", cls: "bg-pink-500 text-white shadow-[0_0_25px_rgba(236,72,153,0.35)]", testid: "home-scan-btn" },
  { label: "Play solo", sub: "Just you vs the bots", icon: Zap, to: "/setup", cls: "bg-cyan-400 text-slate-950 shadow-[0_0_25px_rgba(34,211,238,0.35)]", testid: "home-solo-btn" },
  { label: "Join a group", sub: "Team up with regulars", icon: Users, to: "/search?tab=groups", cls: "bg-purple-500 text-white shadow-[0_0_25px_rgba(168,85,247,0.35)]", testid: "home-group-btn" },
];

export default function HomeScreen() {
  const navigate = useNavigate();
  const { profile, logout } = useApp();
  const streak = weeklyCount(profile.history);

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-8 pt-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Cheers, {profile.handle}</p>
          <h1 className="font-display text-4xl font-black tracking-tight text-slate-50 leading-none mt-1">
            Hey {profile.name.split(" ")[0]} 👋
          </h1>
          <p className="text-slate-400 mt-1">What's the play tonight?</p>
          <div data-testid="home-streak-pill" className="inline-flex items-center gap-1.5 mt-3 bg-orange-500/15 border border-orange-500/40 rounded-full px-3 py-1">
            <Flame size={14} className="text-orange-400" />
            <span className="text-sm font-bold text-orange-300">{streak} this week</span>
            <span className="text-[10px] text-slate-500">· {resetLabel()}</span>
          </div>
        </div>
        <button data-testid="home-logout-btn" onClick={logout} className="text-slate-500 hover:text-red-400 p-2">
          <LogOut size={20} strokeWidth={2.5} />
        </button>
      </div>

      <div className="space-y-4">
        {ACTIONS.map((a, i) => {
          const Icon = a.icon;
          return (
            <motion.button
              key={a.label}
              data-testid={a.testid}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(a.to)}
              className={`w-full rounded-3xl p-6 flex items-center gap-4 text-left no-tap-highlight ${a.cls}`}
            >
              <div className="bg-slate-950/15 rounded-2xl p-3">
                <Icon size={30} strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-black text-xl leading-tight">{a.label}</div>
                <div className="text-sm font-semibold opacity-80">{a.sub}</div>
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="mt-8">
        <OffersStrip />
      </div>

      <div className="mt-4 bg-slate-900 rounded-2xl p-5 border border-slate-800">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Your run</p>
        <div className="flex gap-6">
          <div>
            <span className="font-display text-3xl font-black text-amber-400">{profile.history.length}</span>
            <span className="text-slate-400 text-sm ml-1">quizzes</span>
          </div>
          <div>
            <span className="font-display text-3xl font-black text-pink-400">{profile.badges.length}</span>
            <span className="text-slate-400 text-sm ml-1">badges</span>
          </div>
          <div>
            <span className="font-display text-3xl font-black text-cyan-400">{profile.groups.length}</span>
            <span className="text-slate-400 text-sm ml-1">groups</span>
          </div>
        </div>
      </div>
    </div>
  );
}
